/**
 * Transient messages, with an optional action.
 *
 * The action slot is what makes "Marked done" recoverable: the toast carries
 * an Undo button rather than the app asking "are you sure?" before every
 * completion. Confirmation dialogs on a frequent, cheap action are friction;
 * an undo on the rare mistake is not.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type ToastTone = 'info' | 'success' | 'error'

interface ToastAction {
  label: string
  onClick: () => void
}

interface Toast {
  id: number
  message: string
  tone: ToastTone
  action?: ToastAction
}

interface ToastOptions {
  tone?: ToastTone
  action?: ToastAction
  durationMs?: number
}

interface ToastApi {
  show: (message: string, options?: ToastOptions) => void
  error: (message: string) => void
  success: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const DEFAULT_MS = 4000
/** Errors stay longer: they usually carry something to read and act on. */
const ERROR_MS = 7000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = nextId.current++
      const tone = options.tone ?? 'info'
      const duration = options.durationMs ?? (tone === 'error' ? ERROR_MS : DEFAULT_MS)
      setToasts((current) => [
        // Cap the stack. A burst of failures should not bury the page.
        ...current.slice(-2),
        { id, message, tone, action: options.action },
      ])
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      )
    },
    [dismiss],
  )

  // Clear pending timers on unmount so nothing fires into a dead tree.
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  const value = useMemo<ToastApi>(
    () => ({
      show,
      error: (message: string) => show(message, { tone: 'error' }),
      success: (message: string) => show(message, { tone: 'success' }),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Bottom-centre on a phone (thumb reach), bottom-right on a desktop.
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={[
              'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-md px-4 py-3 text-sm shadow-lg',
              toast.tone === 'error'
                ? 'bg-crimson text-white'
                : toast.tone === 'success'
                  ? 'bg-navy text-white'
                  : 'bg-gray-900 text-white',
            ].join(' ')}
          >
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="shrink-0 rounded-sm px-2 py-1 font-semibold underline underline-offset-2 hover:bg-white/15"
                onClick={() => {
                  toast.action?.onClick()
                  dismiss(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              aria-label="Dismiss"
              className="shrink-0 rounded-sm px-1 text-white/70 hover:text-white"
              onClick={() => dismiss(toast.id)}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}
